// PouncePlanScreen.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Pounce agent banne se THEEK pehle wali screen: plan chuno.
//
//   • Do plan — PAYG ($70 / booked appointment) aur Subscribe ($199/mo, 4
//     included, $50 overage). PAYG pehle se selected hai.
//   • Card selection or collection happens in Stripe Embedded Checkout after
//     the user starts the trial.
//
// Host (BusinessStep / Pounce onboarding) ko sirf `onContinue({ plan,
// paymentMethodId })` milta hai — agent banana host ka kaam hai.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import styles from "./PouncePlanScreen.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import { getPouncePlanOptions } from "../../Store/apiStore";
import { detectCheckoutCurrency, currencySymbol } from "../../lib/checkoutCurrency";

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const PhoneIcon = () => (
  <svg {...svgProps}>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
  </svg>
);

const CrownIcon = () => (
  <svg {...svgProps}>
    <path d="M3 8l4.5 4L12 5l4.5 7L21 8l-1.8 10H4.8L3 8z" />
    <path d="M5 21h14" />
  </svg>
);

// Phone with a sparkle — the Outbound Caller Pro card.
const OutboundIcon = () => (
  <svg {...svgProps}>
    <path d="M19 15.4v2.6a1.8 1.8 0 0 1-2 1.8 17.6 17.6 0 0 1-7.7-2.7 17.3 17.3 0 0 1-5.3-5.3A17.6 17.6 0 0 1 1.3 4 1.8 1.8 0 0 1 3.1 2h2.6a1.8 1.8 0 0 1 1.8 1.5c.1.9.3 1.7.6 2.5a1.8 1.8 0 0 1-.4 1.9L6.6 9a14 14 0 0 0 5.3 5.3l1.1-1.1a1.8 1.8 0 0 1 1.9-.4c.8.3 1.6.5 2.5.6a1.8 1.8 0 0 1 1.6 2z" />
    <path d="M18 2l.9 2.1L21 5l-2.1.9L18 8l-.9-2.1L15 5l2.1-.9L18 2z" />
  </svg>
);

const GiftIcon = () => (
  <svg {...svgProps}>
    <path d="M20 12v9H4v-9" />
    <path d="M2 7h20v5H2z" />
    <path d="M12 21V7" />
    <path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill="currentColor" opacity="0.16" />
    <path
      d="M6 10.3l2.6 2.6L14 7.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const POUNCE_PLANS = [
  {
    id: "payg",
    name: "Pay As You Go",
    price: "$70",
    unit: "booked meeting",
    billed: "No monthly commitment",
    Icon: PhoneIcon,
    points: ["No monthly commitment", "Pay only for results", "Great for getting started"],
  },
  {
    id: "subscribe",
    name: "Outbound Caller Pro",
    price: "$199",
    unit: "month",
    billed: "Billed monthly",
    Icon: CrownIcon,
    recommended: true,
    points: [
      "4 bookings included per month",
      "$50 per additional booking",
      "Best value for growing businesses",
    ],
  },
];

const DEFAULT_PLAN = "payg";

// A customer on a lead deal (the default) sees only that deal, as Outbound
// Caller Pro, in the featured card layout.
const dealPlan = (deal) => ({
  id: "subscribe",
  name: "Outbound Caller Pro",
  featured: true,
  price: `${currencySymbol(deal.currency)}${deal.monthlyPrice}`,
  unit: "month",
  billed: "Billed monthly",
  Icon: OutboundIcon,
  points: [
    `${deal.includedLeads} New Unique Lead Conversations included per month`,
    deal.extraLeadPrice
      ? `${currencySymbol(deal.currency)}${deal.extraLeadPrice} per additional New Unique Lead Conversation`
      : `Calls pause after ${deal.includedLeads} conversations until next month`,
    "AI qualification & appointment booking",
    "No charge for voicemail or unanswered calls",
  ],
});

const PouncePlanScreen = ({
  initialPlan = DEFAULT_PLAN,
  onPlanChange,
  onBack,
  onContinue,
  busy = false,
}) => {
  const [plan, setPlan] = useState(
    POUNCE_PLANS.some((p) => p.id === initialPlan) ? initialPlan : DEFAULT_PLAN,
  );
  // undefined = still loading, null = standard plans.
  const [deal, setDeal] = useState(undefined);
  // Plan options failed to load: offer a retry, never fall back to the
  // standard plans (most customers are on the default deal).
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  const choosePlan = (id) => {
    setPlan(id);
    onPlanChange?.(id);
  };

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    setDeal(undefined);
    // Canada sees CAD prices; every other country USD.
    detectCheckoutCurrency()
      .then((currency) => getPouncePlanOptions(currency))
      .then((d) => {
        if (!alive) return;
        setDeal(d);
        if (d) choosePlan("subscribe");
      })
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry]);

  const loading = deal === undefined && !loadError;
  const plans = loadError ? [] : deal ? [dealPlan(deal)] : POUNCE_PLANS;
  const canContinue = !!plan && !busy && !loading && !loadError;
  const heading = deal ? "Plan Details" : "Choose Plan";

  return (
    <div className={styles.page}>
      {/* Baaki Outbound pages jaisa common HeaderBar: back arrow, bada title,
          neeche chhota "Outbound Caller" (h6 ko neeche .header h6 rakhta hai).
          The deal is a single plan, so it is "Plan Details", not a choice. */}
      <div className={styles.header}>
        <HeaderBar
          title={heading}
          subtitle="Outbound Caller"
          backgroundColor="#fff"
          // Checkout chal raha ho to back band — pehle button disabled tha.
          onBack={() => {
            if (!busy) onBack?.();
          }}
        />
      </div>
      <p className={styles.subtitle}>
        {!deal
          ? "Pick the plan that fits your business."
          : deal.trialDays === 0
            ? "Add AI outbound calling for new leads."
            : "Start with a free trial, then continue on Outbound Caller Pro."}
      </p>

      <div className={styles.plans} role="radiogroup" aria-label={heading}>
        {loading && (
          <div className={styles.plansLoading} role="status">
            <span className={styles.plansSpinner} aria-hidden="true" />
            Loading your plans…
          </div>
        )}
        {loadError && (
          <div className={styles.plansLoading} role="alert">
            We couldn't load your plan.{" "}
            <button type="button" onClick={() => setRetry((n) => n + 1)}>
              Try again
            </button>
          </div>
        )}
        {!loading && plans.map((p) => {
          const selected = plan === p.id;
          const { Icon } = p;
          // The single deal plan: already chosen, so no radio.
          if (p.featured) {
            // A customer's own deal can have no trial (trialDays 0).
            const hasTrial = deal?.trialDays !== 0;
            return (
              <div key={p.id} className={styles.featuredCard}>
                {/* Purple hero: plan ka naam aur price, upar trial badge. */}
                <div className={styles.hero}>
                  <div className={styles.heroTop}>
                    <span className={styles.featuredIcon} aria-hidden="true">
                      <Icon />
                    </span>
                    {hasTrial && (
                      <span className={styles.heroBadge}>
                        <GiftIcon />
                        {deal.trialDays}-Day Free Trial
                      </span>
                    )}
                  </div>
                  <span className={styles.featuredName}>{p.name}</span>
                  <span className={styles.featuredPrice}>
                    {p.price}
                    <span className={styles.featuredUnit}>/ {p.unit}</span>
                  </span>
                </div>

                <div className={styles.featuredBody}>
                  {/* Trial → paid, do steps ki chhoti timeline. */}
                  {hasTrial && (
                    <ol className={styles.steps}>
                      <li className={`${styles.step} ${styles.stepActive}`}>
                        <span className={styles.stepDot} aria-hidden="true" />
                        <span className={styles.stepText}>
                          <strong>{deal.trialDays}-Day Free Trial</strong>
                          <span>
                            Ends after {deal.trialDays} days or {deal.trialLeads} new leads, whichever comes first.
                          </span>
                        </span>
                      </li>
                      <li className={styles.step}>
                        <span className={styles.stepDot} aria-hidden="true" />
                        <span className={styles.stepText}>
                          <strong>Then {p.price}/{p.unit}</strong>
                          <span>Continue on {p.name}.</span>
                        </span>
                      </li>
                    </ol>
                  )}

                  <div className={styles.sectionLabel}>What's included</div>
                  <span className={styles.featuredPoints}>
                    {p.points.map((pt) => (
                      <span key={pt} className={styles.point}>
                        <CheckIcon />
                        {pt}
                      </span>
                    ))}
                  </span>

                  <div className={styles.infoBox}>
                    <svg className={styles.infoIcon} viewBox="0 0 20 20" aria-hidden="true">
                      <circle cx="10" cy="10" r="10" fill="currentColor" />
                      <path d="M10 9v5M10 6.2v.1" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span>
                      A New Unique Lead Conversation is counted when a new lead answers and engages with the AI.
                      Voicemail, no-answer, failed calls, and busy calls are not counted.
                    </span>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={busy}
              className={`${styles.plan} ${selected ? styles.planSelected : ""}`}
              onClick={() => choosePlan(p.id)}
            >
              {p.recommended && <span className={styles.badge}>Recommended</span>}
              {/* /plans ke card jaisa: upar rang wala hissa (icon + daam + bada
                  naam), neeche features. */}
              <span className={styles.planTopSection}>
                <span className={styles.planHead}>
                  <span className={styles.planIcon} aria-hidden="true">
                    <Icon />
                  </span>
                  <span className={styles.planHeadText}>
                    <span className={styles.planSubPrice}>
                      {p.price}/{p.unit}
                    </span>
                    {p.billed && <span className={styles.planBilled}>{p.billed}</span>}
                  </span>
                  <span className={styles.radio} aria-hidden="true" />
                </span>
                <span className={styles.planName}>{p.name}</span>
              </span>
              <span className={styles.planPoints}>
                {p.points.map((pt) => (
                  <span key={pt} className={styles.point}>
                    <CheckIcon />
                    {pt}
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </div>

      {/* Standard plans only; the deal card carries its own trial and info. */}
      {!loading && !loadError && !deal && (
        <p className={styles.trial}>
          <span className={styles.trialIcon} aria-hidden="true">
            <GiftIcon />
          </span>
          <span>
            <strong>14-day free trial</strong> or 2 booked appointments, whichever comes first.
          </span>
        </p>
      )}

      {/* Shared AnimatedButton, Qualifying Questions jaisa hi. Wo apne aap
          position: fixed hota hai; hamara .footer pehle se fixed hai, isliye
          use relative karke footer ke andar baithaya. */}
      <div className={styles.footer}>
        <div className={styles.continueWrap}>
          <AnimatedButton
            label={deal?.trialDays === 0 ? "Add Outbound Caller" : "Start Free Trial"}
            onClick={() => onContinue?.({ plan, deal: deal || null })}
            disabled={!canContinue}
            isLoading={busy}
            positionType="relative"
            bottom="0px"
            width="100%"
            bgSrc="/svg/svg-theme2.svg"
          />
        </div>
      </div>
      {busy && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingCard} role="status" aria-live="polite">
            <div className={styles.loadingIcon} aria-hidden="true">
              <span className={styles.spinner} />
              <img src="/svg/outbound_caller_icon.svg" alt="" />
            </div>
            <h2>Getting Outbound Caller ready</h2>
            <p>Preparing your secure checkout…</p>
            <span className={styles.loadingHint}>This may take a few moments.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PouncePlanScreen;
